/**
 * Client-side prediction + server reconciliation. (§2.3, §2.4, §2.7)
 *
 * No Three.js, no DOM, no wall-clock reads beyond what the caller passes in —
 * this file is pure logic so it can be tested headlessly, which matters because
 * it is the hardest thing in the client to get right and the easiest to break.
 *
 * ── The model ──────────────────────────────────────────────────────────────
 *
 * Every input is applied to the local world IMMEDIATELY (§2.3 step 3) and the
 * resulting state is stored in a ring. When the server tells us which input it
 * last processed (§2.4), we compare its truth against what we predicted at that
 * same seq. Divergence beyond POSITION_EPSILON means we rewind to the server's
 * state and replay everything the server hasn't seen yet, landing back at
 * "now" — corrected, without losing the inputs in flight.
 *
 * ── Deviation from §2.4, stated plainly ────────────────────────────────────
 *
 * §2.4 reads as "if diverged: snap and re-simulate; else: do nothing". Taken
 * literally that FREEZES every remote player. The local sim has inputs for one
 * player only — its own (see `inputSlots` below); remote players receive `null`
 * inputs and therefore stand still in the predicted world. So "do nothing" means
 * the other 21 capsules never move again until the local player happens to
 * diverge. The brief's §2.4 is written about the local player and is silent on
 * this because §2.5 renders remotes from interpolation, not from this world.
 *
 * So we split the rebase in two, which costs nothing and is what §2.4 means:
 *
 *   - ALWAYS rebase remote players, the ball and match state to server truth.
 *     Cheap (a field copy), no re-simulation, keeps the predicted world's
 *     collision environment honest for the local player.
 *   - ONLY rewind + replay when the LOCAL player diverged > POSITION_EPSILON.
 *     That is the expensive path and it is exactly the one §2.4 gates.
 *
 * ── Why "converge", not "match" ────────────────────────────────────────────
 *
 * A replayed tick is not bit-identical to the server's. Snapshots deliberately
 * omit the RNG (snapshot.ts header) and Rapier's contact caches do not survive a
 * restore (DECISIONS.md D1). Both are known and both are ~orders of magnitude
 * below POSITION_EPSILON. If you are here because clients drift under packet
 * loss: that is D1, it is by design, and this file absorbing it is the design
 * working. Do not "fix" it by shipping RNG state.
 */

import {
  FIXED_DT,
  INPUT_BUFFER_CAPACITY,
  MAX_PLAYERS,
  MAX_ROLLBACK_TICKS,
  POSITION_EPSILON,
  RECONCILE_SMOOTH_MS,
  allocWorldLike,
  cloneWorldInto,
  createStepResult,
  emptyInput,
  step,
  type InputCommand,
  type PhysicsWorld,
  type StepResult,
  type WorldState,
} from '@fg/shared/sim';
import { NO_INPUT_SEQ, dequantizeFrameInto, type SnapshotFrame } from '@fg/shared/net';

/** What a reconcile actually did — surfaced for the dev overlay and tests. */
export const enum ReconcileOutcome {
  /** Local player matched the server inside POSITION_EPSILON. No replay. */
  Clean = 0,
  /** Diverged: rewound to server truth and replayed the unacked inputs. */
  Corrected = 1,
  /**
   * Beyond MAX_ROLLBACK_TICKS, or no stored prediction for the acked seq.
   * The world is snapped and the visual offset is NOT smoothed — §2.2 says
   * beyond 500ms you take the teleport.
   */
  HardSnap = 2,
}

export interface ReconcileStats {
  outcome: ReconcileOutcome;
  /** Metres between our prediction at the acked seq and the server's truth. */
  error: number;
  /** Inputs replayed on the Corrected path. */
  replayed: number;
}

/** One stored prediction: the input and the world it produced. */
interface PredictionSlot {
  /** -1 = empty. Guards against a stale ring entry masquerading as valid. */
  seq: number;
  input: InputCommand;
  /** World state AFTER `input` was applied. */
  world: WorldState;
}

function copyInputInto(dst: InputCommand, src: InputCommand): void {
  dst.seq = src.seq;
  dst.tick = src.tick;
  dst.moveX = src.moveX;
  dst.moveZ = src.moveZ;
  dst.aimYaw = src.aimYaw;
  dst.liftBias = src.liftBias;
  dst.curl = src.curl;
  dst.passCharge = src.passCharge;
  dst.shootCharge = src.shootCharge;
  dst.flags = src.flags;
  dst.skill = src.skill;
  dst.tactical = src.tactical;
}

/**
 * Copy the snapshot-carried fields of one player from `src` to `dst`.
 * Mirrors exactly what snapshot.ts puts on the wire — nothing else, because
 * nothing else is authoritative here.
 */
function copyPlayerFields(dst: WorldState, src: WorldState, i: number): void {
  const a = dst.players;
  const b = src.players;
  a.active[i] = b.active[i]!;
  a.team[i] = b.team[i]!;
  a.px[i] = b.px[i]!;
  a.py[i] = b.py[i]!;
  a.pz[i] = b.pz[i]!;
  a.vx[i] = b.vx[i]!;
  a.vy[i] = b.vy[i]!;
  a.vz[i] = b.vz[i]!;
  a.yaw[i] = b.yaw[i]!;
  a.stamina[i] = b.stamina[i]!;
  a.anim[i] = b.anim[i]!;
  a.animBlend[i] = b.animBlend[i]!;
  a.skillActive[i] = b.skillActive[i]!;
  a.possessing[i] = b.possessing[i]!;
  a.sentOff[i] = b.sentOff[i]!;
}

function copyBallFields(dst: WorldState, src: WorldState): void {
  const a = dst.ball;
  const b = src.ball;
  a.px = b.px; a.py = b.py; a.pz = b.pz;
  a.vx = b.vx; a.vy = b.vy; a.vz = b.vz;
  a.wx = b.wx; a.wy = b.wy; a.wz = b.wz;
  a.qx = b.qx; a.qy = b.qy; a.qz = b.qz; a.qw = b.qw;
  a.possessedBy = b.possessedBy;
  a.grounded = b.grounded;
}

function copyMatchFields(dst: WorldState, src: WorldState): void {
  const a = dst.match;
  const b = src.match;
  a.phase = b.phase;
  a.restartStage = b.restartStage;
  a.half = b.half;
  a.clockTicks = b.clockTicks;
  a.addedTimeTicks = b.addedTimeTicks;
  a.scoreA = b.scoreA;
  a.scoreB = b.scoreB;
  a.restartTeam = b.restartTeam;
  a.restartX = b.restartX;
  a.restartZ = b.restartZ;
  a.restartTaker = b.restartTaker;
  a.possessionTeam = b.possessionTeam;
  a.advantageTicks = b.advantageTicks;
  a.pendingFoulPlayer = b.pendingFoulPlayer;
  a.pendingFoulSeverity = b.pendingFoulSeverity;
  a.excitement = b.excitement;
}

export interface PredictionOptions {
  /** Seed world — the client builds this from the Welcome payload. */
  template: WorldState;
  physics: PhysicsWorld;
  /** PlayerId this client drives. Mutable via `setLocalPlayer` (§4.8 switching). */
  localPlayerId: number;
}

export class PredictionSystem {
  /** The live predicted world. Render the local player from this (§2.3). */
  readonly world: WorldState;

  private readonly physics: PhysicsWorld;
  private readonly ring: PredictionSlot[] = [];
  /** Decode target for incoming snapshots. Only snapshot-carried fields are read. */
  private readonly auth: WorldState;
  /** step()'s input array: MAX_PLAYERS long, only the local slot ever set. */
  private readonly inputSlots: Array<InputCommand | null> = [];
  private readonly stepOut: StepResult = createStepResult();

  private localId: number;
  private seqCounter = NO_INPUT_SEQ;
  /** True once at least one snapshot has been decoded into `auth`, so a rebase can
   *  seed the newly-driven player from real server truth rather than the template. */
  private authValid = false;

  /**
   * Rendered-minus-physical offset for the local player, in metres. The physics
   * body is corrected instantly; the MESH is drawn at physics + this, and this
   * decays to zero. §2.4: "The player must never see a snap."
   */
  visualOffsetX = 0;
  visualOffsetY = 0;
  visualOffsetZ = 0;

  private lastStats: ReconcileStats = { outcome: ReconcileOutcome.Clean, error: 0, replayed: 0 };

  constructor(opts: PredictionOptions) {
    this.physics = opts.physics;
    this.localId = opts.localPlayerId;
    this.world = allocWorldLike(opts.template);
    this.auth = allocWorldLike(opts.template);

    // Allocate the ENTIRE ring up front (§2.3, §15.7). Sixty-four worlds is
    // ~64 x 22 x a handful of typed arrays — trivial memory, and it means the
    // rewind path never touches the allocator, which is the whole point: a GC
    // pause during reconciliation is exactly the stutter we are preventing.
    for (let i = 0; i < INPUT_BUFFER_CAPACITY; i++) {
      this.ring.push({ seq: -1, input: emptyInput(), world: allocWorldLike(opts.template) });
    }
    for (let i = 0; i < MAX_PLAYERS; i++) this.inputSlots.push(null);
  }

  get localPlayerId(): number {
    return this.localId;
  }

  /**
   * §4.8 player switching — adopt a new driven player and rebase onto it.
   *
   * This is the client's half of the switch handshake. The SERVER decides the new
   * id (it sends ControlAssign; see net/connection.ts + game/playerSwitch.ts
   * `wireSwitching`); this makes prediction follow it without a teleport:
   *
   *   1. clear the input ring — the stored predictions were validated against the
   *      OLD player's positions and say nothing about the new one; comparing them
   *      would "correct" a divergence that never happened (proven in the test);
   *   2. adopt the new id;
   *   3. seed the new player from the latest server snapshot, so the first predict
   *      runs from where the SERVER has them, not from a stale predicted ghost —
   *      and from finite numbers, so a rebase can never introduce a NaN.
   *
   * Remote interpolation is untouched (that is interpolation.ts's buffer, a
   * different code path); the visual handoff is the camera blend in
   * game/playerSwitch.ts, not a mesh teleport here — the mesh of the newly-driven
   * player is simply drawn at its own position.
   */
  rebaseControlled(id: number): void {
    if (id === this.localId) return;
    this.localId = id;
    this.reset();
    // Seed from server truth when we have any. Before the first snapshot `auth`
    // is only the template, and the world already holds the best estimate, so
    // skip the copy rather than stamp template values over it.
    if (this.authValid) copyPlayerFields(this.world, this.auth, id);
  }

  /** @deprecated Use `rebaseControlled`. Kept as the name the optimistic client
   *  path (main.ts) historically called; identical behaviour. */
  setLocalPlayer(id: number): void {
    this.rebaseControlled(id);
  }

  get stats(): ReconcileStats {
    return this.lastStats;
  }

  /** Next seq this system will hand out. Inputs are numbered from 1 (welcome.ts). */
  get nextSeq(): number {
    return this.seqCounter + 1;
  }

  /** Drop all stored predictions. The world itself is left alone. */
  reset(): void {
    for (const slot of this.ring) slot.seq = -1;
    this.visualOffsetX = 0;
    this.visualOffsetY = 0;
    this.visualOffsetZ = 0;
  }

  private slotFor(seq: number): PredictionSlot | null {
    if (seq <= NO_INPUT_SEQ) return null;
    const slot = this.ring[seq % INPUT_BUFFER_CAPACITY];
    if (!slot || slot.seq !== seq) return null;
    return slot;
  }

  /**
   * Stamp `input` with the next seq, apply it to the local world immediately,
   * and store the result. Returns the stored input — the caller sends THIS
   * (its seq is now authoritative) and keeps it in the redundancy window (§2.3).
   *
   * The returned reference is owned by the ring and is overwritten once
   * INPUT_BUFFER_CAPACITY further inputs have been recorded. That is far longer
   * than the ~8-input redundancy window, so senders may hold it safely.
   */
  predict(input: InputCommand): InputCommand {
    const seq = ++this.seqCounter;
    const slot = this.ring[seq % INPUT_BUFFER_CAPACITY]!;

    input.seq = seq;
    input.tick = this.world.tick + 1; // the tick this input is FOR
    copyInputInto(slot.input, input);
    slot.seq = seq;

    this.applyOne(slot.input);
    cloneWorldInto(slot.world, this.world);
    return slot.input;
  }

  /** One tick of the local sim with only the local player's input populated. */
  private applyOne(input: InputCommand): void {
    this.inputSlots[this.localId] = input;
    step(this.world, this.inputSlots, this.stepOut, this.physics);
    this.inputSlots[this.localId] = null;
  }

  /**
   * Fold a server snapshot into the predicted world. (§2.4)
   *
   * @param frame               Decoded snapshot — borrowed, not retained.
   * @param lastProcessedInputSeq  The server's ack for THIS client.
   */
  reconcile(frame: SnapshotFrame, lastProcessedInputSeq: number): ReconcileStats {
    // Decode server truth into scratch. Only the fields snapshot.ts actually
    // carries are meaningful in `auth`; everything else there is stale junk and
    // is never read below.
    dequantizeFrameInto(frame, this.auth);
    this.authValid = true;

    const local = this.localId;
    const beforeX = this.world.players.px[local]!;
    const beforeY = this.world.players.py[local]!;
    const beforeZ = this.world.players.pz[local]!;

    const acked = this.slotFor(lastProcessedInputSeq);
    const pending = this.seqCounter - lastProcessedInputSeq;

    // No usable history: first snapshot after Welcome (ack is NO_INPUT_SEQ), a
    // seq that fell out of the ring, or a rewind deeper than §2.2 allows.
    if (acked === null || pending < 0 || pending > MAX_ROLLBACK_TICKS) {
      this.hardSnap(frame);
      this.lastStats = { outcome: ReconcileOutcome.HardSnap, error: 0, replayed: 0 };
      return this.lastStats;
    }

    // Divergence is measured at the acked seq — comparing "now" against a
    // snapshot from the past would report our own legitimate prediction lead as
    // error and correct us into the past, every single snapshot.
    const dx = acked.world.players.px[local]! - this.auth.players.px[local]!;
    const dy = acked.world.players.py[local]! - this.auth.players.py[local]!;
    const dz = acked.world.players.pz[local]! - this.auth.players.pz[local]!;
    const error = Math.hypot(dx, dy, dz);

    if (error <= POSITION_EPSILON) {
      // Local player is fine. Still rebase everything we do NOT predict, or the
      // other 21 players freeze (see the header).
      this.rebaseNonLocal();
      this.lastStats = { outcome: ReconcileOutcome.Clean, error, replayed: 0 };
      return this.lastStats;
    }

    // ── Rewind ──────────────────────────────────────────────────────────────
    // Restore our own prediction at the acked seq first: it carries the fields
    // the snapshot omits (rng, action/skill timers, charges) at roughly the
    // right moment, which is the closest estimate available. Then overwrite
    // everything the server IS authoritative for.
    cloneWorldInto(this.world, acked.world);
    dequantizeFrameInto(frame, this.world);

    // ── Replay ──────────────────────────────────────────────────────────────
    // Every input the server has not yet seen, in order, to land back at "now".
    let replayed = 0;
    for (let seq = lastProcessedInputSeq + 1; seq <= this.seqCounter; seq++) {
      const slot = this.slotFor(seq);
      if (slot === null) continue; // hole in the ring; skipping beats desyncing
      this.applyOne(slot.input);
      cloneWorldInto(slot.world, this.world);
      replayed++;
    }

    // The physics body has moved. Push the difference into the visual offset so
    // the MESH stays where it was and eases across (§2.4 step 5).
    this.visualOffsetX += beforeX - this.world.players.px[local]!;
    this.visualOffsetY += beforeY - this.world.players.py[local]!;
    this.visualOffsetZ += beforeZ - this.world.players.pz[local]!;

    this.lastStats = { outcome: ReconcileOutcome.Corrected, error, replayed };
    return this.lastStats;
  }

  /** Server truth for everything this client does not predict. */
  private rebaseNonLocal(): void {
    for (let i = 0; i < MAX_PLAYERS; i++) {
      if (i === this.localId) continue;
      copyPlayerFields(this.world, this.auth, i);
    }
    // The ball is the server's, always (§2.7). The local player predicts it
    // between snapshots when possessing; at snapshot time the server wins.
    copyBallFields(this.world, this.auth);
    copyMatchFields(this.world, this.auth);
  }

  /**
   * §2.2: beyond MAX_ROLLBACK_TICKS, hard snap. The visual offset is cleared
   * rather than smoothed — easing a 500ms+ correction would slide the player
   * across the pitch for a second, which reads far worse than the teleport.
   */
  private hardSnap(frame: SnapshotFrame): void {
    dequantizeFrameInto(frame, this.world);
    this.reset();
  }

  /**
   * Decay the visual offset toward zero. Call once per rendered frame.
   *
   * Exponential, with RECONCILE_SMOOTH_MS as the time constant: ~63% gone after
   * one window, ~95% after three. Frame-rate independent because the decay is
   * exp(-dt/tau), not a fixed per-frame fraction — the naive `offset *= 0.9`
   * would correct twice as fast at 120fps as at 60, so the same packet loss
   * would feel different on different machines.
   */
  updateVisualSmoothing(dtS: number): void {
    if (dtS <= 0) return;
    const tau = RECONCILE_SMOOTH_MS / 1000;
    const keep = Math.exp(-dtS / tau);
    this.visualOffsetX *= keep;
    this.visualOffsetY *= keep;
    this.visualOffsetZ *= keep;
    // Snap the tail to zero: an offset of 1e-9 m is not worth carrying, and
    // leaving it means the value never actually reaches zero.
    if (Math.abs(this.visualOffsetX) < 1e-4) this.visualOffsetX = 0;
    if (Math.abs(this.visualOffsetY) < 1e-4) this.visualOffsetY = 0;
    if (Math.abs(this.visualOffsetZ) < 1e-4) this.visualOffsetZ = 0;
  }

  /** Total un-eased visual correction still outstanding, in metres. */
  get visualOffsetMagnitude(): number {
    return Math.hypot(this.visualOffsetX, this.visualOffsetY, this.visualOffsetZ);
  }
}

// ---------------------------------------------------------------------------
// Ball prediction policy (§2.7)
// ---------------------------------------------------------------------------

/**
 * How long after a local kick the client keeps rendering its own predicted ball.
 *
 * §2.7 wants the ball to leave the foot instantly for feel, with the server
 * overriding on the next snapshot. The moment you shoot you STOP possessing, so
 * a bare `possessing` test would hand the ball back to interpolation on the very
 * tick the shot fires — rendering it still at your feet for a full interpolation
 * delay, which is precisely the lag §2.7 exists to hide. This window covers the
 * gap until the server's version of the shot arrives: one snapshot at 20Hz is
 * 50ms, plus a half-RTT, plus the interpolation delay.
 */
export const BALL_PREDICT_AFTER_KICK_MS = 250;

/**
 * Should the ball be drawn from the predicted world rather than interpolated?
 * (§2.7 — "the client predicts the ball ONLY when the local player possesses
 * it", extended by the kick window above.)
 */
export function shouldPredictBall(
  world: WorldState,
  localPlayerId: number,
  msSinceLocalKick: number,
): boolean {
  if (world.ball.possessedBy === localPlayerId) return true;
  return msSinceLocalKick >= 0 && msSinceLocalKick < BALL_PREDICT_AFTER_KICK_MS;
}

/** Ticks of prediction the client is running ahead of the server, for the HUD. */
export function predictionLeadTicks(rttMs: number): number {
  if (rttMs < 0) return 0;
  return Math.round(rttMs / 2 / (FIXED_DT * 1000));
}
